import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconSearch, IconAlertTriangle, IconActivity } from "@tabler/icons-react";
import dayjs from "dayjs";
import { KeyConstant, LangConstant, SystemConstant } from "const";
import { SystemErrorTable, UserActivityTable } from "./components";
import { StorageUtils, SystemUtils } from "utils";
import { PageTitle } from "components";
import { useSystemTracking } from "hooks";

const SystemTracking = () => {
  const { t: getLabel } = useTranslation(LangConstant.NS_COMMON);

  const sidebarTitles = getLabel(LangConstant.OBJ_SIDEBAR_TITLE, { returnObjects: true });
  const branchErrorObj = getLabel(LangConstant.OBJ_BRANCH_ERROR, { returnObjects: true });

  const [fromDate, setFromDate] = useState(dayjs().subtract(7, "days").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [tab, setTab] = useState(SYSTEM_ERRORS_TABLE);
  const [systemErrorTablePage, setSystemErrorTablePage] = useState(1);
  const [userActivitiesTablePage, setUserActivitiesTablePage] = useState(1);

  const handleGetPayload = () => {
    const page = tab === SYSTEM_ERRORS_TABLE ? systemErrorTablePage : userActivitiesTablePage;
    const fromDateConvert = dayjs(fromDate).startOf("day").valueOf();
    const toDateConvert = dayjs(toDate).endOf("day").valueOf();
    return {
      tab: tab,
      branchId: StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ID),
      paging: 1,
      asc: 0,
      offset: (page - 1) * LIMITATION,
      limit: LIMITATION,
      sinceTime: fromDateConvert,
      lastTime: toDateConvert,
      orderBy: "created",
    };
  };

  const { systemErrors, userActivities, fetchSystemErrors, fetchUserActivities, isLoadingSystemErrors, isLoadingUserActivities } = useSystemTracking(
    handleGetPayload(),
  );

  const handleFromDateChange = event => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = event => {
    setToDate(event.target.value);
  };

  const handleSearch = (from, to, type) => {
    const fromDateConvert = dayjs(from).startOf("day").valueOf();
    const toDateConvert = dayjs(to).endOf("day").valueOf();
    if (!Boolean(fromDateConvert) || !Boolean(toDateConvert)) {
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_DATE_NULL), "error");
    } else if (fromDateConvert > toDateConvert) {
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_INVALID_DATE), "error");
    } else if (type === SYSTEM_ERRORS_TABLE) {
      fetchSystemErrors();
    } else if (type === USER_ACTIVITIES_TABLE) {
      fetchUserActivities();
    }
  };

  const handleClickSearch = () => {
    handleSearch(fromDate, toDate, tab);
  };

  const handleChangeTab = newTab => {
    setTab(newTab);
    setSystemErrorTablePage(1);
    setUserActivitiesTablePage(1);
    setFromDate(dayjs().subtract(7, "days").format("YYYY-MM-DD"));
    setToDate(dayjs().format("YYYY-MM-DD"));
  };

  useEffect(() => {
    const type =
      tab === SYSTEM_ERRORS_TABLE
        ? SystemConstant.SYSTEM_TRACKING_TYPE.errorsList
        : SystemConstant.SYSTEM_TRACKING_TYPE.userActivities;
    handleSearch(fromDate, toDate, type);
  }, [tab, systemErrorTablePage, userActivitiesTablePage]);

  return (
    <Fragment>
      <PageTitle>{sidebarTitles.systemTracking}</PageTitle>

      <div className="card">
        {/* Tabs in card header */}
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${tab === SYSTEM_ERRORS_TABLE ? "active" : ""}`}
                onClick={() => handleChangeTab(SYSTEM_ERRORS_TABLE)}
              >
                <IconAlertTriangle size={16} className="me-1" />
                {getLabel(LangConstant.TXT_SYSTEM_ERROR_LIST)}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${tab === USER_ACTIVITIES_TABLE ? "active" : ""}`}
                onClick={() => handleChangeTab(USER_ACTIVITIES_TABLE)}
              >
                <IconActivity size={16} className="me-1" />
                {getLabel(LangConstant.TXT_USER_ACTIVITY_LIST)}
              </button>
            </li>
          </ul>
        </div>

        {/* Filters */}
        <div className="card-body border-bottom">
          <div className="d-flex align-items-end gap-2">
            <div>
              <label className="form-label">{branchErrorObj.fromDate}</label>
              <input
                type="date"
                className="form-control"
                style={{ width: 180 }}
                value={fromDate}
                onChange={handleFromDateChange}
                max={dayjs().format("YYYY-MM-DD")}
              />
            </div>
            <div>
              <label className="form-label">{branchErrorObj.toDate}</label>
              <input
                type="date"
                className="form-control"
                style={{ width: 180 }}
                value={toDate}
                onChange={handleToDateChange}
                max={dayjs().format("YYYY-MM-DD")}
              />
            </div>
            <button className="btn btn-primary d-flex align-items-center gap-1" onClick={handleClickSearch}>
              <IconSearch size={16} />
              {getLabel(LangConstant.TXT_SEARCH_BUTTON)}
            </button>
          </div>
        </div>

        {/* Table content */}
        <div className="card-body">
          {tab === SYSTEM_ERRORS_TABLE && (
            <SystemErrorTable
              limitation={LIMITATION}
              page={systemErrorTablePage}
              setPage={setSystemErrorTablePage}
              branchErrors={systemErrors || { data: [], totalElements: 0 }}
              isLoading={isLoadingSystemErrors}
            />
          )}
          {tab === USER_ACTIVITIES_TABLE && (
            <UserActivityTable
              limitation={LIMITATION}
              page={userActivitiesTablePage}
              setPage={setUserActivitiesTablePage}
              userActivities={userActivities || { data: [], totalElements: 0 }}
              isLoading={isLoadingUserActivities}
            />
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default SystemTracking;

const SYSTEM_ERRORS_TABLE = 0;
const USER_ACTIVITIES_TABLE = 1;
const LIMITATION = 10;
